import { BrowserMultiFormatReader } from '@zxing/browser';
import { use, useEffect, useMemo, useRef, useState } from 'react';
import { CheckButton, CloseButton, StyleButton } from './Assets';
import './BarcodeScanner.scss';
import Store from './Store';
import cellMerge from './assets/cell_merge.svg';
import zoomIn from './assets/zoom_in.svg';
import iconSun from './assets/sun.svg';
import UI from './lib.r2/ui/ui-controls';
import SoundUtils from './lib.r2/utils/SoundUtils';
import PinchZoomControl from './lib.r3/utils/PinchZoomControl';



//============================================================================= BarcodeScanner Component
export default function BarcodeScanner(props){
	const viewRef = useRef();
	const videoRef = useRef();
	const _videoRef = useRef();
	const selectedDeviceId = useRef();
	const enabled = useRef(true);
	const devices = useRef()
	const stream = useRef()
	const settings = useRef(null)
	const useNativeSize = useRef(false)

	const [isInit, setInit] = useState(false);
	const [isSelect, setSelect] = useState(false)
	const [info, setInfo] = useState('')
	const [orientation, setOrientation] = useState({horizontal:false, vertical:false})
	const [exposure, setExposure] = useState(0)
	const [showExp, setShowExp] = useState(false)
	const [debugLog, setDebugLog] = useState('')

	const debug = useMemo(() => {
		return {
			log: (value) => {
				setDebugLog(e => e + value + '\n')
			},
			clear: () => {
				setDebugLog('')
			}
		}
	}, [])

	const isTest = Store.use('isTest')

	const [setup] = useState(e => {
		return {
			codeReader : new BrowserMultiFormatReader(),
			pc: new PinchZoomControl({useLimits:true}),
		}
	})


	// ------------------------------------------------------------------------ mount
	useEffect(() => {
		// ---init BarcodeScanner
		global.cameraInit = init
		Store._cameraStop = stop

		const lastExposure = localStorage.getItem('cameraExposure')
		if (lastExposure){
			const exp = parseInt(lastExposure)
			setExposure(exp)
		}

		return () => {	//cleanup (unmount)
			// console.info('---unmount Scanner');
			stop();
		}
	}, []);



	// ------------------------------------------------------------------------ useEffect
	useEffect(() => {
		// console.info('---BarcodeScanner selected', props.selected, props.mode, isInit);

		if (props.selected){
			openBarcodeView()
		}else{
			stopScan()
		}

	}, [props.selected]);




	// ------------------------------------------------------------------------ openBarcodeView
	const openBarcodeView = async e => {
		if (!isInit) await init()

		onUpdateView()

		if (props.mode === 'scan' || props.mode === 'gtin'){
			setOrientation({horizontal:false, vertical:false})
			setup.pc.setTarget(null)
			startScan()

		}else{		//photo
			setOrientation(setup.orientation)
			setup.pc.setTarget(videoRef.current)
			setup.pc.autoScale()
		}
	}


	// ------------------------------------------------------------------------ init
	const init = async e => {
		//---orientationFlip
		let or = localStorage.getItem('flipOrientation')
		or = or? JSON.parse(or) : {horizontal:false, vertical:false}
		setup.orientation = or
		localStorage.setItem('flipOrientation', JSON.stringify(or))
		setOrientation(or)

		devices.current = await getDevices()	//no devices detected!

		if (!devices.current?.length) return

		let id = localStorage.getItem('cameraDeviceId');

		isTest && alert('last used device-id: ' + JSON.stringify(id))

		if (devices.current.find(e => e.id === id)){
			// ---set device from localStore:
			selectedDeviceId.current = id;
		}else{
			// ---get new device
			selectedDeviceId.current = devices.current[0].id
		}

		// devices:', devices.current)
		// current device:', selectedDeviceId.current)

		if (!await startStream()) return

		setup.pc.onUpdate = () => onUpdateView()

		props.onInit?.()
	}
	

	// ------------------------------------------------------------------------ start
	const startStream = async e => {
		let stream = await startVideo().catch(err => {
			console.error('error!', err)
		});

		if (!stream){
			onError()
			return false
		}

		setInit(true);
		return true
	}


	// ------------------------------------------------------------------------ start
	const startVideo = async e => {
		if (!navigator.mediaDevices) return

		// start stream: Brute-Force camera resolution detection ...
		// test 2000 ...
		let _stream
		let videoOptions = {
			deviceId: selectedDeviceId.current,
			width: { min: 1000 },
			height: { min: 1000 },
			facingMode: 'environment',
			autoGainControl: true,
			exposureMode: 'continuous',
			whiteBalanceMode: 'continuous',
			focusMode: 'continuous',
			sharpness: 6,
			// brightness: 0,
			// contrast: 30,
			// iso: 1000,
		}
		
		// test if min size of 1000px is possible ...
		_stream = await navigator.mediaDevices.getUserMedia({
			video: videoOptions
		}).catch(err => {
			console.error('constraints error!', err)
		})

		if (!_stream){			// test default ...
			console.log('use default constraints ...')
			_stream = await navigator.mediaDevices.getUserMedia({
				video: {
					deviceId: selectedDeviceId.current,
				}
			})
		}

		stream.current = _stream

		if (!_stream){ // no stream detected!
			return
		}
		// ok

		// ---start stream
		_videoRef.current.srcObject = _stream;

		const [track] = _stream.getVideoTracks();
		if (!track) return

		const _settings = track.getSettings();
		_settings._label = track.label
		if (!_settings) return

		settings.current = _settings

		console.log('---video options:', videoOptions)
		console.log('---stream track:', track)
		console.log('---capabilities:', track.getCapabilities())
		console.log('---constraints used:', track.getConstraints())
		console.log('---stream settings:', _settings)
		console.log('---window.devicePixelRatio:', window.devicePixelRatio)
		// window.devicePixelRatio=2;

// setup.pc.onUpdate = () => onUpdateView()

		// setTimeout(() => {
		// 	onUpdateView()
		// }, 100);
		
		return _stream
	}


	// ------------------------------------------------------------------------ startScan
	const startScan = async e => {
		clearInterval(setup.interval)
		setup.codeTest = null
		setup.interval = setInterval(e => onScan(), 200)		//5 times per second
	}


	// ------------------------------------------------------------------------ onScan
	const onScan = async e => {
		let v = _videoRef.current
		if (!v) return
		
		let c1 = setup.c1
		let c2 = setup.c2

		if (!c1){
			c1 = setup.c1 = document.getElementById('canvas-test-1') || document.createElement('canvas')
			c2 = setup.c2 = document.getElementById('canvas-test-2') || document.createElement('canvas')

			// https://html.spec.whatwg.org/multipage/canvas.html#concept-canvas-will-read-frequently
			setup.c1ctx = c1.getContext('2d', {willReadFrequently:true})
			setup.c2ctx = c2.getContext('2d', {willReadFrequently:true})
		}

		c1.width = v.videoWidth
		c1.height = v.videoHeight

		c2.width = v.videoHeight
		c2.height = v.videoWidth

		setup.c2ctx.translate(c2.width, 0)
		setup.c2ctx.rotate(0.5 * Math.PI)			//rotate 90 deg

		// Apply exposure filter to canvas context for barcode scanning (iOS compatible)
		if (exposure){
			let v = 1 + (exposure / 100)
			let c = 1 + (exposure / 100) * 0.1
			setup.c1ctx.filter = 'brightness(' + v + ') contrast(' + c + ')';
		}
		setup.c1ctx.drawImage(v, 0, 0)

		let res
		try {
			// https://github.com/zxing-js/browser
			// res = await setup.codeReader.decodeFromVideoDevice(selectedDeviceId.current) 			//first scan test
			// res = await setup.codeReader.decodeFromStream(stream.current)		//first scan test
			// res = await setup.codeReader.decodeFromVideoElement(v)
			res = setup.codeReader.decodeFromCanvas(c1)			//first scan test

		}catch(err){	//try rotated canvas if first test is not success
			// Apply exposure filter to rotated canvas context (iOS compatible)
			if (exposure){
				let v = 1 + (exposure / 100)
				let c = 1 + (exposure / 100) * 0.1
				setup.c2ctx.filter = 'brightness(' + v + ') contrast(' + c + ')';
			}
			setup.c2ctx.drawImage(v, 0, 0)

			try {
				res = setup.codeReader.decodeFromCanvas(c2)		//second scan test
			}catch(err){
				return
			}
		}

		if (res?.text !== setup.codeTest){		//not match
			setup.codeTest = res.text			//cache first detection
			return
		}

		stopScan()

		SoundUtils.beep();

		let code = res.text
		if (code.length > 14) code = code.substring(2,14)	//trim code to 14 digits
	
		props.onResult?.(code, c1);
	}


	// ------------------------------------------------------------------------ onSelectDevice
	const onSelectDevice = async id => {
		// devices:', JSON.stringify(devices.current))
		
		if (!devices.current.find(e => e.id === id)){
			alert('selected camera not found! ' + id)
			return
		}

		stop();

		setSelect(false)

		isTest && alert('save selected device-id: ' + JSON.stringify(id))

		selectedDeviceId.current = id;
		localStorage.setItem('cameraDeviceId', id);

		await startStream()

		if (props.mode !== 'photo') startScan()
	}


	// ------------------------------------------------------------------------ stop
	const stop = () => {
		let video = _videoRef.current

		stopScan()

		try {
			if (video){
				let stream = video.srcObject;
				stream?.getTracks().forEach(track => track.stop())
			
				video.pause()
				video.srcObject = null
				video.src = null
			}
		} catch (error) {
			console.error(error)
		}

		stream.current = null
		setInfo('')
		setInit(false);
	}


	// ------------------------------------------------------------------------ stopScan
	const stopScan = () => {
		clearInterval(setup.interval)
		delete setup.c1
		delete setup.c2
		delete setup.c1ctx
		delete setup.c2ctx
		delete setup.codeTest

		// setExposure(0)
		setShowExp(false)
	}


	// ------------------------------------------------------------------------ getDevices
	const getDevices = async e => {

		if (!navigator.mediaDevices){
			alert('no media devices found!')
			return null
		}

		console.log('---supported constraints:', navigator.mediaDevices.getSupportedConstraints());
		
		if (navigator.mediaDevices.getUserMedia) {

			// --- first allow access
			let devs = await navigator.mediaDevices.enumerateDevices();

			// --- try again whe access approved
			devs = await navigator.mediaDevices.enumerateDevices();
			
			if (!devs){
				alert('no enumerated devices found!')
				return null;
			}

			let list = [];
			devs.forEach(dev => {
				if (dev.kind === 'videoinput'){
					list.push({label:dev.label || 'Video device 1', id:dev.deviceId});
				}
			});

			return list;

		}else return null;
	}


const [canvas, setCanvas] = useState(null)

	// ------------------------------------------------------------------------ createImage
	const createImage = e => {
		// create image');
		let canvas = getImage();

		// image complete!');
		SoundUtils.beep();
if (props.onPhoto) props.onPhoto(canvas);
// setCanvas(canvas)
	}


	// ------------------------------------------------------------------------ onCalcView
	const getVideoSize = e => {
		let video = _videoRef.current
		let view = viewRef.current
		let cw = view.clientWidth		//view width
		let ch = view.clientHeight		//view height
		let v0 = ch / cw
		let vw, vh
		if (useNativeSize.current){
			vw = settings.current.width		//native camera width
			vh = settings.current.height	//native camera height
		}else{
			vw = video.videoWidth			//original video width
			vh = video.videoHeight			//original video height
		}
		let av = vh / vw				//video aspect ratio
		let pc = setup.pc				//PinchZoomControl instance
		if (!pc) return [vw, vh]

		let w, h
		if (av > v0){
			w = Math.floor(vw / pc.scaleFactor);	//native width from video
			h = Math.floor(w * v0);
		}else{
			h = Math.floor(vh / pc.scaleFactor);	//native height from video
			w = Math.floor(h / v0);
		}
		return [w, h]
	}


	// ------------------------------------------------------------------------ getImage
	const getImage = e => {
		let video = _videoRef.current
		let view = viewRef.current
		let cw = view.clientWidth		//view width
		let ch = view.clientHeight		//view height
		// let v0 = ch / cw
		let vw = video.videoWidth		//original video width
		let vh = video.videoHeight		//original video height
		// let av = vh / vw				//video aspect ratio
		let pc = setup.pc				//PinchZoomControl instance

		// let w, h
		// if (av > v0){
		// 	w = Math.floor(vw / pc.scaleFactor);	//native width from video
		// 	h = Math.floor(w * v0);
		// }else{
		// 	h = Math.floor(vh / pc.scaleFactor);	//native height from video
		// 	w = Math.floor(h / v0);
		// }

		const [w, h] = getVideoSize();

		let canvas = document.createElement('canvas')		//final image canvas
		canvas.width = w
		canvas.height = h
		let ctx = canvas.getContext('2d')

		// Detect iOS devices
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
			(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
		
		// Apply exposure filter to the final canvas context
		if (exposure && !isIOS){
			let v = 1 + (exposure / 100)
			let c = 1 + (exposure / 100) * 0.1
			// Use more aggressive filter values for better visibility
			let brightness = Math.max(0.1, Math.min(3.0, v))
			let contrast = Math.max(0.1, Math.min(3.0, c))
			
			ctx.filter = `brightness(${brightness}) contrast(${contrast})`;
			console.log('---applying CSS filter to final canvas:', ctx.filter, 'exposure:', exposure, 'brightness:', brightness, 'contrast:', contrast);
		}

		let oCanvas = document.createElement('canvas')		//original video canvas
		oCanvas.width = vw
		oCanvas.height = vh
		let oCtx = oCanvas.getContext('2d')

		let or = setup.orientation
		oCtx.scale(or.horizontal? -1 : 1, or.vertical? -1 : 1);
		oCtx.translate(or.horizontal? -vw : 0, or.vertical? -vh : 0);
		oCtx.drawImage(video, 0, 0)

		let w0 = cw / pc.scale
		let h0 = ch / pc.scale
		let x0 = -pc.pos.x / pc.scale
		let y0 = -pc.pos.y / pc.scale
		ctx.drawImage(oCanvas, x0, y0, w0, h0, 0, 0, canvas.width, canvas.height);

		console.log('---image scale:', pc.scaleFactor,
			'stage:', cw, ch,
			'nativeSize:', useNativeSize.current,
			'track:', settings.current.width, settings.current.height,
			'video:', vw, vh,
			'image:', w, h,
		)

		// Apply manual filter only on iOS devices if exposure is set
		if (exposure && isIOS){
			applyManualFilter(canvas, exposure);
		}

		// oCanvas = ColorUtils.adjustValue(oCanvas, 0.2);

		return canvas;
	}


	// ------------------------------------------------------------------------ onUpdateView
	const onUpdateView = () => {
		let video = _videoRef.current

		if (!settings.current) return
		const s = settings.current
		const [w, h] = getVideoSize()
		setInfo(
			s._label + ' | ' + Math.round(s.frameRate) + 'fps\n'
			+ 'track size: ' + s.width + ' x ' + s.height
			+ '\nvideo size: ' + video.videoWidth + ' x ' + video.videoHeight
			+ '\nimage size: ' + w + ' x ' + h + ' scaling: ' + (setup.pc?.scaleFactor || 1).toFixed(2)
		)
	}


	// ------------------------------------------------------------------------ applyManualFilter
	const applyManualFilter = (canvas, exposure) => {
		const ctx = canvas.getContext('2d');
		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
		const data = imageData.data;
		
		let v = 1 + (exposure / 100);
		let c = 1 + (exposure / 100) * 0.1;
		
		// Clamp values for safety
		v = Math.max(0.1, Math.min(3.0, v));
		c = Math.max(0.1, Math.min(3.0, c));
		
		console.log('---applying manual filter:', 'brightness:', v, 'contrast:', c, 'exposure:', exposure);
		
		// Apply brightness and contrast to each pixel
		for (let i = 0; i < data.length; i += 4) {
			// Get RGB values
			let r = data[i];
			let g = data[i + 1];
			let b = data[i + 2];
			
			// Apply brightness
			r = r * v;
			g = g * v;
			b = b * v;
			
			// Apply contrast
			r = ((r - 128) * c) + 128;
			g = ((g - 128) * c) + 128;
			b = ((b - 128) * c) + 128;
			
			// Clamp values to 0-255
			data[i] = Math.max(0, Math.min(255, r));
			data[i + 1] = Math.max(0, Math.min(255, g));
			data[i + 2] = Math.max(0, Math.min(255, b));
		}
		
		// Put the modified image data back
		ctx.putImageData(imageData, 0, 0);
	}


	// ------------------------------------------------------------------------ onClose
	const onClose = e => {
		stopScan()
		props.onClose?.()
	}


	// ------------------------------------------------------------------------ onError
	const onError = e => {
		console.info('---error')
		enabled.current = false
		stop()
		setTimeout(e => {
			props.onError?.()
		}, 500)
	}


	// ------------------------------------------------------------------------ changeOrientation
	const onChangeOrientation = (dir) => {
		let or = {...orientation}
		if (dir === 'h') or.horizontal = !or.horizontal
		if (dir === 'v') or.vertical = !or.vertical

		// change orientation:', or)
		localStorage.setItem('flipOrientation', JSON.stringify(or))
		setup.orientation = or
		setOrientation(or)
	}
	

	// ------------------------------------------------------------------------ changeOrientation
	const onZoomReset = () => {
		setup.pc.setTarget(videoRef.current)
		setup.pc.autoScale()
	}


	if (!enabled.current) return null

	// ........................................................................ render
	return (
		<div className='zxing' ref={viewRef}
			onClick={e => {
// debug.log('onClick: ' + e.target.className)
				if (e.target.className.includes('webcam')) setShowExp(false)
			}}		//click outside sun icon
			onTouchStart={e => {
// debug.log('onTouchStart: ' + e.target.className)
				if (e.target.className.includes('webcam')) setShowExp(false)
			}}
			>
			<div className={'webcam ' + (props.mode === 'photo'? 'photo' : 'scan')} ref={videoRef}>
				<video
					ref={_videoRef}
					autoPlay playsInline muted
					style={{
						transform: 'scale(' + (orientation.horizontal? -1 : 1) + ', ' + (orientation.vertical? -1 : 1) + ')',
						filter: (exposure?
							'brightness(' + (1 + (exposure / 100)) + ') contrast(' + (1 + (exposure / 100) * 0.1) + ')'
							: 'none')
					}}
				/>
			</div>

			{props.mode === 'photo' &&
				<div className='webcam-photo-button' onClick={createImage}></div>
			}

			{/* <canvas id='canvas-test-3' /> */}

			{isInit &&
				<div className='scan-header'>
					<UI.IconButton className='scan-menu'
						onClick={e => setSelect(true)}
						size={24}
						width={60}
						height={60}
					>☰</UI.IconButton>

					{/* {props.mode === 'photo'&& */}
						<div className='icon-bar'>
							<img className='icon-zoom-reset'
								src={zoomIn} alt='icon-zoom-reset'
								onClick={onZoomReset}
							/>
							<img className={'icon-turn horizontal' + (orientation.horizontal? ' selected' : '')}
								src={cellMerge} alt='icon-turn-horizontal'
								onClick={e => onChangeOrientation('h')}
							/>
							<img className={'icon-turn vertical' + (orientation.vertical? ' selected' : '')}
								src={cellMerge} alt='icon-turn-vertical'
								onClick={e => onChangeOrientation('v')}
							/>
							<img className={'icon-sun' + (orientation.vertical? ' selected' : '')}
								src={iconSun} alt='icon-sun'
								onClick={e => setShowExp(!showExp)}
							/>
						</div>
					{/* } */}

					<CloseButton onClick={onClose} />

					{showExp &&
					// {(showExp && props.mode === 'photo') &&
						<ValueSlider value={exposure} onChange={
							value => {
								setExposure(value)
								localStorage.setItem('cameraExposure', value)
							}
						} />
					}

					{debugLog &&
						<div className='scanner-debug-view'>
							{debugLog}
							<CloseButton onClick={e => {
								console.log('clear debug log')
								debug.clear()
							}} />
						</div>
					}
				</div>
			}

			{isSelect && 		//show device list
				<div className='device-list'>
					<div className='device-list-label'>{global.getLabel('select-device')}</div>
					{devices.current.map((e, i) => 
						<div className='device-item'
							key={i}
							onClick={() => onSelectDevice(e.id)}
							>{e.label}</div>
					)}
					<StyleButton
						label={global.getLabel('close')}
						onClick={e => {
							e.preventDefault()
							setSelect(false)
						}}
						style={{opacity:0.5}}
					/>

					<div className='native-camera-size'>
						<div>use native camera size</div>
						<CheckButton checked={useNativeSize.current} onClick={e => {
							useNativeSize.current = !useNativeSize.current
							onUpdateView()
						}} />
					</div>

				</div>
			}

			{!isInit &&
				<UI.Backdrop spinner title={global.getLabel('open-camera')}>
					<StyleButton label={global.getLabel('cancel')} onClick={onClose} />
				</UI.Backdrop>
			}

			<div className='webcam-info'>{info}</div>

			{canvas &&
				<div className='canvas-preview' onClick={e => {
					e.preventDefault()
					e.stopPropagation()
					setCanvas(null)
				}}>
					<img src={canvas.toDataURL()} alt='canvas-preview' />
				</div>
			}
		</div>
	)
}



// ------------------------------------------------------------------------ ValueSlider
const ValueSlider = (props) => {
	const [value, setValue] = useState(props.value || 0)
	const [isDragging, setIsDragging] = useState(false)
	const sliderRef = useRef()
	const { onChange } = props

	useEffect(() => {
		onChange?.(value)
	}, [value, onChange])

	const handleChange = (e) => {
		setValue(e.target.value)
	}

	const handleTouchStart = (e) => {
		setIsDragging(true)
		e.preventDefault()
	}

	const handleTouchMove = (e) => {
		if (!isDragging) return
		
		e.preventDefault()
		
		const slider = sliderRef.current
		if (!slider) return
		
		const rect = slider.getBoundingClientRect()
		const touch = e.touches[0]
		const x = touch.clientX - rect.left
		const percentage = Math.max(0, Math.min(1, x / rect.width))
		
		const min = props.min || -50
		const max = props.max || 50
		const newValue = min + (percentage * (max - min))
		
		setValue(Math.round(newValue))
	}

	const handleTouchEnd = (e) => {
		setIsDragging(false)
		e.preventDefault()
	}

	return (
		<div className='value-slider'>
			<input 
				ref={sliderRef}
				type='range'
				min={props.min || -50}
				max={props.max || 50}
				step={0}
				value={value}
				onChange={handleChange}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				style={{ touchAction: 'none' }}
			/>
			<div className='value-label'>{value + '%'}</div>
		</div>
	)
}
